import { createRef } from 'react';
import _ from 'lodash';

import request from '../../utils/request';

import { getPathVariables } from '../../misc';
import { getValueFromEligibleStep } from '../formSpecificLogic';

import {
  fieldBusinessConnectEdition,
  fieldPlan,
  keyChannelId,
  keyDirectoryListingDetails,
  keyDirectoryListingType,
  keyWhiteGloveLocations,
  contentFulFields,
  prequalificationForm,
  fieldBusinessConnectEditionWorkplace,
  keyOutlet
} from '../../utils/constant';

const formFieldsGroup = 'formFieldsGroup';
const formSection = 'formSection';
const userDetailsKey = 'userDetails';
const repeatedFormKey = 'repeatedForm';

const profile = request.getProfile();

const getDefaultValue = (key, value, orderData, formFields) => {
  if (orderData && orderData.id) {
    return key === fieldBusinessConnectEdition && !orderData[key]
      ? getValueFromEligibleStep(orderData)
      : orderData[key];
  }

  return key === fieldBusinessConnectEdition && !value
    ? getValueFromEligibleStep(formFields)
    : value || '';
};

export const findField = (data, fieldName) => {
  let result = null;
  let i = 0;

  if (!data) return result;

  while (i < data.length && !result) {
    const item = data[i];
    const {
      sys: {
        contentType: {
          sys: { id }
        }
      },
      fields: itemFields
    } = item;
    if (id === formFieldsGroup) {
      result = findField(itemFields.fields, fieldName);
    } else if (id === formSection) {
      result = findField(itemFields.formFields, fieldName);
    } else if (itemFields.name === fieldName) {
      result = item;
    }
    i += 1;
  }

  return result;
};

export const calcFieldMonthlyPayment = (
  fieldName,
  fieldValue,
  formStepFields
) => {
  let result = 0;

  if (fieldValue) {
    const field = findField(formStepFields, fieldName);
    const currentOption =
      field &&
      field.fields.options.find(item => item.fields.value === fieldValue);
    if (currentOption) {
      result = currentOption.fields.monthlyPayment;
    }
  }

  return result;
};

export const getMultiCheckboxOptions = (
  options = [],
  value = [],
  fieldName,
  formFields = {},
  orderData = {}
) => {
  return options.map(op => {
    return {
      ...op,
      checked:
        value.includes(op.fields.value) ||
        (!_.get(formFields, `${fieldName}.touched`, false) &&
          !_.has(orderData, fieldName) &&
          op.fields.isAutoSelected)
    };
  });
};

export const normalizedFields = (
  data,
  initFields,
  orderData,
  isSalesSupportFlow,
  localizedStrings,
  isVarAgent
) => {
  let result = initFields;
  if (data) {
    data.forEach(item => {
      const { sys, fields } = item;
      const {
        name,
        type,
        required,
        value,
        minValue,
        maxValue,
        minLength,
        maxLength,
        price,
        monthlyPayment,
        options,
        resetFields,
        customResetFields
      } = fields;
      const {
        contentType: {
          sys: { id }
        }
      } = sys;
      const key = name;
      const ref = createRef();

      if (id === formFieldsGroup) {
        result = normalizedFields(
          fields.fields,
          result,
          orderData,
          isSalesSupportFlow,
          localizedStrings,
          isVarAgent
        );
      } else if (id === formSection) {
        result = normalizedFields(
          fields.formFields,
          result,
          orderData,
          isSalesSupportFlow,
          localizedStrings,
          isVarAgent
        );
      } else if (id === userDetailsKey) {
        result = normalizedFields(
          fields.formStepFields,
          result,
          orderData,
          isSalesSupportFlow,
          localizedStrings,
          isVarAgent
        );
      } else if (id === repeatedFormKey) {
        const {
          dataMap,
          key: repeatedFormKeyName,
          repeatBasedOnBackendField
        } = fields;
        const formObjectKeys = fields.formFields.reduce(
          (acc, { fields: { name: formNameKey, value: defaultValue } }) => {
            acc[formNameKey] = defaultValue;
            return acc;
          },
          {}
        );
        if (repeatedFormKeyName === keyDirectoryListingDetails) {
          const {
            contentfulValues: {
              directoryListingValues: {
                valuePrimaryDirectoryListing,
                valueExtraListing,
                valueTwoExtraListing,
                valueThreeExtraListing
              }
            }
          } = localizedStrings;

          const directoryListingType =
            orderData[keyDirectoryListingType] &&
            orderData[keyDirectoryListingType].includes(
              valuePrimaryDirectoryListing
            )
              ? valuePrimaryDirectoryListing
              : '';
          const extraListings = [
            valueExtraListing,
            valueTwoExtraListing,
            valueThreeExtraListing
          ];
          const primaryDirectoryListingType =
            directoryListingType === valuePrimaryDirectoryListing
              ? orderData[keyDirectoryListingType] &&
                orderData[keyDirectoryListingType].find(listing =>
                  extraListings.includes(listing)
                )
              : '';

          let numberOfDirectoryListing = dataMap[directoryListingType];
          if (primaryDirectoryListingType) {
            numberOfDirectoryListing += dataMap[primaryDirectoryListingType];
          }

          const locale = getPathVariables(window.location.pathname)[0];
          const fieldsArray = [];
          for (let i = 0; i < numberOfDirectoryListing; i += 1) {
            const newValue =
              orderData &&
              orderData[keyDirectoryListingDetails] &&
              orderData[keyDirectoryListingDetails][i]
                ? {
                    ...formObjectKeys,
                    ...orderData[keyDirectoryListingDetails][i]
                  }
                : { ...formObjectKeys };

            fieldsArray.push({
              ...newValue,
              title: dataMap[`${locale}-${i}`]
            });
          }
          result[fields.key] = fieldsArray;
        } else if (repeatedFormKeyName === keyWhiteGloveLocations) {
          const newValue =
            orderData && orderData[keyWhiteGloveLocations]
              ? orderData[keyWhiteGloveLocations]
              : [];
          let diff = orderData[repeatBasedOnBackendField] - newValue.length;
          while (diff > 0) {
            newValue.push({ ...formObjectKeys });
            diff -= 1;
          }
          result[fields.key] = [...newValue];
        } else {
          result[fields.key] = [formObjectKeys];
        }
      } else {
        const orderDataKey = key.includes('vpbOfficeSeatRate')
          ? 'vpbOfficeSeatRate'
          : key;
        const defaultValue = getDefaultValue(
          orderDataKey,
          value,
          orderData,
          fields.formFields
        );

        let updatedUpfrontPayment = price;
        let updatedMonthlyPayment = [fieldPlan].includes(key)
          ? calcFieldMonthlyPayment(fieldPlan, defaultValue, fields.formFields)
          : monthlyPayment;

        /**
         * Adding monthly & upfront price for option selected to the field properties
         */
        if (['Select', 'Radio'].includes(type) && defaultValue) {
          const selectedOption = options.find(
            option => option.fields.value === defaultValue
          );
          updatedMonthlyPayment = _.get(
            selectedOption,
            'fields.monthlyPayment',
            0
          );
          updatedUpfrontPayment = _.get(selectedOption, 'fields.price', 0);
        }

        result[key] = {
          ref,
          type,
          value: defaultValue,
          initPrice: updatedUpfrontPayment || 0,
          price: updatedUpfrontPayment || 0,
          initMonthlyPayment: updatedMonthlyPayment || 0,
          monthlyPayment: updatedMonthlyPayment || 0,
          minValue,
          maxValue,
          minLength,
          maxLength,
          isRequired: required || false,
          isVisible: true,
          options,
          touched: false
        };

        if (resetFields) {
          result[key].resetFields = resetFields;
        }

        if (customResetFields) {
          result[key].customResetFields = customResetFields;
        }

        if (type === 'MultiCheckbox')
          result[key].options = getMultiCheckboxOptions(
            options,
            defaultValue,
            key,
            initFields,
            orderData
          );

        if (type === 'Checkbox') {
          result[key].checked = defaultValue;
        }
      }
    });
  }

  if (isVarAgent) {
    const ref = createRef();
    result[keyOutlet] = {
      value: '',
      isRequired: true,
      isVisible: true,
      ref
    };
  }

  if (isSalesSupportFlow && _.get(profile, 'channels', []).length > 1) {
    const ref = createRef();
    result[keyChannelId] = {
      value: (orderData && orderData[keyChannelId]) || '',
      isRequired: true,
      isVisible: true,
      ref
    };
  }
  return result;
};

// Custom logic for "Business Connect Edition"
export const checkBusinessConnectEdition = (name, orderData, formName, yes) => {
  if (name === fieldBusinessConnectEdition) {
    const { telusWorkplace } = contentFulFields[prequalificationForm];
    if (orderData[telusWorkplace] === yes) {
      return fieldBusinessConnectEditionWorkplace;
    }
  }

  return name;
};

export const getFieldName = (field, orderData, formName, yes) => {
  const result = field && field.fields ? field.fields.name : '';
  return checkBusinessConnectEdition(result, orderData, formName, yes);
};

export const getFieldOption = (option, fieldName, fields) =>
  fields &&
  Object.prototype.hasOwnProperty.call(fields, fieldName) &&
  fields[fieldName].isVisible
    ? fields[fieldName][option]
    : '';

export const getFieldValue = (fieldName, fields) =>
  getFieldOption('value', fieldName, fields);

export const getFieldType = (fieldName, fields) =>
  getFieldOption('type', fieldName, fields);

export const getFieldChecked = (fieldName, fields) =>
  getFieldOption('checked', fieldName, fields);
